import React from 'react';
import type { Crop } from 'react-image-crop';
import { CheckIcon } from '@assets/icons';
import {
  PhotoXPosition,
  PhotoYPosition,
  useUpdatePageContainerMutation,
  useUploadPhotoFromUrlMutation,
  useWebsiteCreateOrUpdatePagePhotoPresentationMutation,
} from '@graphql/generated';
import type { EventPageType, PhotoDataFragment, SetAssetPayload, WebsitePagePhotoPresentationFragment } from '@graphql/generated';
import { useTranslation } from '@shared/core';
import { useToast } from '@withjoy/joykit';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { useFocusPointEditorTelemetry } from './FocusPointEditor.telemetry';
import type { ImageData } from './FocusPointEditor.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addError } from '@shared/utils/logger';
import { useIsPhotoUploadFromCollectionEnabled } from './useIsPhotoUploadFromCollectionEnabled';

interface FullWidthEditorProps
  extends Readonly<{
    eventId: string;
    pageId?: string;
    pageType?: EventPageType;
    pageSlug?: string;
    updatePagePhotoPreview?: (filename: string, imageInfo: PhotoDataFragment, photoPresentation?: Maybe<WebsitePagePhotoPresentationFragment>) => void;
    refetchQueryList?: ReadonlyArray<string>;
    telemetryCategory: string;
    handleOnCancel: () => void;
    handleCancelClick: () => void;
    imageData: ImageData;
  }> {}

export const useFullWidthEditor = ({
  eventId,
  updatePagePhotoPreview,
  pageSlug,
  pageType,
  pageId,
  refetchQueryList = [],
  telemetryCategory,
  handleOnCancel,
  handleCancelClick,
  imageData,
}: FullWidthEditorProps) => {
  const { toast } = useToast();
  const { tq } = useTranslation('joykit');

  const isPhotoUploadFromCollectionEnabled = useIsPhotoUploadFromCollectionEnabled(pageType);

  const { pagePhotoUpdated } = useFocusPointEditorTelemetry();

  const [createOrUpdatePagePhotoPresentation, { loading: isSavingPagePhotoPresentation }] = useWebsiteCreateOrUpdatePagePhotoPresentationMutation({
    onCompleted: data => {
      switch (data.websiteCreateOrUpdatePagePhotoPresentation?.__typename) {
        // Success
        case 'websiteCreateOrUpdatePagePhotoPresentationResponse': {
          toast(tq`Photo settings updated successfully.`, { icon: <CheckIcon /> });
          // Using `handleCancelClick` since that's also used to just close the editor (as previously done).
          handleCancelClick();
          break;
        }

        // Error
        case 'websiteCreateOrUpdatePagePhotoPresentationResponseError': {
          toast(tq`Failed to update photo settings.`, { icon: <WarningTriangle /> });
          const { message, id, pageId } = data.websiteCreateOrUpdatePagePhotoPresentation;
          addError('WebsitePagePhotoEditor - FullWidthEditor - set page photo presentation mutation', {
            status: 'error',
            message,
            eventId,
            id,
            pageId,
            photoId: imageData.id,
            telemetryCategory,
            pageType,
          });
          break;
        }
      }
    },

    onError: error => {
      toast(tq`Failed to update photo settings.`, { icon: <WarningTriangle /> });
      addError('WebsitePagePhotoEditor - FullWidthEditor - set page photo presentation mutation', {
        status: 'fail',
        error,
        eventId,
        photoId: imageData.id,
        telemetryCategory,
        pageType,
      });
    },
  });

  const [updatePageContainerPhotoPresentation, { loading: isSavingPageContainerPhotoPresentation }] = useUpdatePageContainerMutation({
    onCompleted: () => toast(tq`Photo settings updated successfully.`, { icon: <CheckIcon /> }),
    onError: () => toast(tq`Failed to update photo settings.`, { icon: <WarningTriangle /> }),
  });
  const [uploadPhotoFromUrl, { loading: waitingUploadPhoto }] = useUploadPhotoFromUrlMutation();

  const handleOnCustomPagePhotoSave = useEventCallback(
    async ({
      uploadedImagePhotoId,
      uploadedImageData,
      photoPresentationLayout,
      crop,
    }: {
      uploadedImagePhotoId?: string;
      uploadedImageData?: SetAssetPayload;
      photoPresentationLayout: 'fullWidth' | 'pageWidth';
      crop: Crop;
    }) => {
      let mainPhotoId: string | null | undefined = null;

      // if the photo is uploaded from collection feature is enabled, then we don't need to upload the photo again, it is handled by the PhotoPicker component
      if (isPhotoUploadFromCollectionEnabled && uploadedImagePhotoId) {
        mainPhotoId = uploadedImagePhotoId;
      } else {
        if (uploadedImageData && imageData?.url && pageId && uploadedImageData.assetId) {
          const { data } = await uploadPhotoFromUrl({
            variables: {
              payload: {
                assetId: uploadedImageData.assetId,
                containerId: pageId,
                url: imageData.url,
              },
            },
          });
          mainPhotoId = data?.uploadPhotoFromUrl?.id;
          pagePhotoUpdated(telemetryCategory, 'Update', 'succeeded', { pageType: pageSlug, actualPageType: pageType });
        }
      }
      updatePageContainerPhotoPresentation({
        variables: {
          payload: {
            id: pageId || '',
            ...(mainPhotoId && { mainPhotoId }),
            mainPhotoCropInfo: {
              ...crop,
              unit: 'percent',
            },
            mainPhotoPresentationLayout: photoPresentationLayout,
          },
        },
        refetchQueries: ['PageContainerBySlug', 'WebsiteInfo', ...refetchQueryList],
        awaitRefetchQueries: true,
      }).then(info => {
        if (!info.errors) {
          pagePhotoUpdated(telemetryCategory, 'Update', 'succeeded', { pageType: pageSlug, actualPageType: pageType });
          handleOnCancel();
          const mainPhoto = info.data?.updatePageContainer.mainPhoto;
          const mainPhotoAlignment = info.data?.updatePageContainer.mainPhotoAlignment;
          const photoPresentation =
            info.data?.updatePageContainer.mainPhotoPresentationLayout && info.data?.updatePageContainer.mainPhotoCropInfo
              ? ({
                  id: info.data.updatePageContainer.id,
                  presentationLayout: info.data?.updatePageContainer.mainPhotoPresentationLayout,
                  cropInfo: {
                    ...info.data.updatePageContainer.mainPhotoCropInfo,
                  },
                } as WebsitePagePhotoPresentationFragment)
              : null;

          if (updatePagePhotoPreview && mainPhoto && photoPresentation) {
            const assetId = uploadedImageData?.assetId || imageData?.assetId || mainPhoto.id;
            updatePagePhotoPreview(
              assetId,
              {
                id: mainPhotoId || mainPhoto.id,
                url: mainPhoto.url,
                assetId,
                height: mainPhoto.height,
                width: mainPhoto.width,
                layout: {
                  alignX: mainPhotoAlignment?.x || PhotoXPosition.center,
                  alignY: mainPhotoAlignment?.y || PhotoYPosition.center,
                },
              },
              photoPresentation,
            );
          }
        }
      });
    },
  );

  const handleOnPagePhotoSave = useEventCallback(
    async ({ uploadedImageData, photoPresentationLayout, crop }: { uploadedImageData?: SetAssetPayload; photoPresentationLayout: 'fullWidth' | 'pageWidth'; crop: Crop }) => {
      createOrUpdatePagePhotoPresentation({
        variables: {
          payload: {
            pageId,
            cropInfo: {
              ...crop,
              unit: 'percent',
            },
            presentationLayout: photoPresentationLayout,
          },
        },
      }).then(info => {
        if (!info.errors) {
          if (
            updatePagePhotoPreview &&
            info.data?.websiteCreateOrUpdatePagePhotoPresentation?.__typename === 'websiteCreateOrUpdatePagePhotoPresentationResponse' &&
            info.data.websiteCreateOrUpdatePagePhotoPresentation.photoPresentation &&
            info.data.websiteCreateOrUpdatePagePhotoPresentation.photoPresentation.photo
          ) {
            updatePagePhotoPreview(
              uploadedImageData?.assetId || '',
              info.data.websiteCreateOrUpdatePagePhotoPresentation.photoPresentation.photo,
              info.data.websiteCreateOrUpdatePagePhotoPresentation.photoPresentation,
            );
          }
        }
      });
    },
  );

  return { handleOnCustomPagePhotoSave, handleOnPagePhotoSave, isSavingPageContainerPhotoPresentation, waitingUploadPhoto, isSavingPagePhotoPresentation };
};
